import React, { useState, useEffect, useMemo } from 'react';
import {
    FiGitCommit, FiServer, FiGitMerge, FiMail, FiRotateCcw, FiUser,
    FiSearch, FiFilter, FiDownload, FiChevronLeft, FiChevronRight, FiChevronUp, FiChevronDown,
    FiX, FiClock, FiFileText, FiCalendar
} from 'react-icons/fi';
import { RiFileExcelFill } from "react-icons/ri";
import api from '../../utils/api';
import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

const GitActivity = () => {
    const [gitlabStats, setGitlabStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Table State
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [selectedCommit, setSelectedCommit] = useState(null);

    // Advanced Filters State
    // Date Helper
    // Date Picker Helpers & Refs
    const dateInputRef = React.useRef(null);
    const toDateInputRef = React.useRef(null);

    const formatDate = (date) => {
        const d = new Date(date);
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
    };

    const formatDateDisplay = (dateStr) => {
        if (!dateStr) return '';
        const parts = String(dateStr).split('-');
        if (parts.length === 3) {
            const [y, m, d] = parts;
            return `${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}/${y}`;
        }
        return dateStr;
    };

    const [dateRange, setDateRange] = useState({
        from: formatDate(new Date()),
        to: formatDate(new Date())
    });
    const [selectedEmployees, setSelectedEmployees] = useState([]); // Array of emails
    const [selectedBranch, setSelectedBranch] = useState('');
    const [selectedProject, setSelectedProject] = useState(''); // Corrected variable name



    const goToPrevDay = () => {
        const current = new Date(dateRange.from);
        current.setDate(current.getDate() - 1);
        const prevStr = formatDate(current);
        setDateRange({ from: prevStr, to: prevStr });
        fetchData(prevStr, prevStr);
    };

    const goToNextDay = () => {
        const current = new Date(dateRange.from);
        current.setDate(current.getDate() + 1);
        const nextStr = formatDate(current);
        const todayStr = formatDate(new Date());

        if (nextStr > todayStr) return;

        setDateRange({ from: nextStr, to: nextStr });
        fetchData(nextStr, nextStr);
    };

    const goToToday = () => {
        const todayStr = formatDate(new Date());
        setDateRange({ from: todayStr, to: todayStr });
        fetchData(todayStr, todayStr);
    };

    const [statusFilters, setStatusFilters] = useState({
        pipeline: false,
        merge: false,
        prod_ready: false,
        revert: false
    });

    const fetchData = React.useCallback(async (start, end) => {
        try {
            setLoading(true);
            const params = {
                start_date: start,
                end_date: end
            };
            const res = await api.get('/hrms/gitlab-stats', { params });
            if (res.data.status) {
                setGitlabStats(res.data.data);
            }
        } catch (error) {
            console.error('Error fetching GitLab stats:', error);
            setError('Failed to load GitLab activity.');
        } finally {
            setLoading(false);
        }
    }, []);

    // Initial Load
    useEffect(() => {
        fetchData(dateRange.from, dateRange.to);
    }, []);

    const handleApplyFilter = () => {
        fetchData(dateRange.from, dateRange.to);
    };

    // Derived Lists for Filters
    const { uniqueEmployees, uniqueProjects, uniqueBranches } = useMemo(() => {
        if (!gitlabStats?.recent_activity) return { uniqueEmployees: [], uniqueProjects: [], uniqueBranches: [] };

        const authors = new Map();
        const projects = new Set();
        const branches = new Set();

        gitlabStats.recent_activity.forEach(item => {
            // Use first_name/last_name from backend if available
            const name = item.first_name ? `${item.first_name} ${item.last_name || ''}` : item.employee_email;
            if (!authors.has(item.employee_email)) {
                authors.set(item.employee_email, { email: item.employee_email, name });
            }
            if (item.project_name) projects.add(item.project_name);
            if (item.branch_name) branches.add(item.branch_name);
        });

        return {
            uniqueEmployees: Array.from(authors.values()).sort((a, b) => a.name.localeCompare(b.name)),
            uniqueProjects: Array.from(projects).sort(),
            uniqueBranches: Array.from(branches).sort()
        };
    }, [gitlabStats]);

    // Grouping Logic - Aggregate lineage by root_commit_id
    const groupedActivity = useMemo(() => {
        if (!gitlabStats?.recent_activity) return [];

        const groups = new Map();
        // Process raw activity
        gitlabStats.recent_activity.forEach(item => {
            const rootId = item.root_commit_id || item.commit_id;
            if (!groups.has(rootId)) {
                groups.set(rootId, []);
            }
            groups.get(rootId).push(item);
        });

        const grouped = Array.from(groups.values()).map(items => {
            // Sort flow by date (oldest first = feature push usually)
            const flow = items.sort((a, b) => new Date(a.commit_datetime) - new Date(b.commit_datetime));

            // Root is the grouping key, usually the initial feature push
            const rootItem = flow[0];
            const latestItem = flow[flow.length - 1];

            // Aggregate Properties
            const branches = Array.from(new Set(flow.map(i => i.branch_name)));
            const isProdReady = flow.some(i => i.production_ready === 'Yes');
            const isReverted = flow.some(i => i.is_reverted == 1);
            const isMerged = flow.some(i => i.merge_status === 'Merged');

            // Determine "Deployed By" if any stage has it
            const deployedBy = flow.find(i => i.deployed_by)?.deployed_by || null;

            return {
                ...rootItem, // Inherit base props (author, message, etc from root)
                latest_commit_datetime: latestItem.commit_datetime, // For sorting by recency
                pipeline_status: latestItem.pipeline_status, // Show latest status
                display_branches: branches, // For displaying lineage
                production_ready: isProdReady ? 'Yes' : 'No',
                is_reverted: isReverted ? 1 : 0,
                merge_status: isMerged ? 'Merged' : (rootItem.merge_status || 'Not Merged'),
                deployed_by_user: deployedBy,
                lineage_events: flow // Store full history for modal
            };
        });

        // Default sort by latest activity desc
        return grouped.sort((a, b) => new Date(b.latest_commit_datetime) - new Date(a.latest_commit_datetime));
    }, [gitlabStats]);

    // Filter Logic
    const filteredActivity = useMemo(() => {
        return groupedActivity.filter(item => {
            // Text Search
            const term = searchTerm.toLowerCase();
            const matchesSearch = (
                (item.project_name && item.project_name.toLowerCase().includes(term)) ||
                (item.display_branches.some(b => b.toLowerCase().includes(term))) ||
                (item.commit_message && item.commit_message.toLowerCase().includes(term)) ||
                (item.employee_email && item.employee_email.toLowerCase().includes(term))
            );

            // Date Range (Check if ANY event in lineage falls in range, or just latest?)
            // Using Latest Activity for filtering
            let matchesDate = true;
            if (dateRange.from) {
                matchesDate = matchesDate && new Date(item.latest_commit_datetime) >= new Date(dateRange.from);
            }
            if (dateRange.to) {
                const toDate = new Date(dateRange.to);
                toDate.setHours(23, 59, 59, 999);
                matchesDate = matchesDate && new Date(item.latest_commit_datetime) <= toDate;
            }

            // Employee Filter
            let matchesEmployee = true;
            if (selectedEmployees.length > 0) {
                matchesEmployee = selectedEmployees.includes(item.employee_email);
            }

            // Project Filter
            let matchesProject = true;
            if (selectedProject) {
                matchesProject = item.project_name === selectedProject;
            }

            // Branch Filter
            let matchesBranch = true;
            if (selectedBranch) {
                matchesBranch = item.display_branches.includes(selectedBranch);
            }

            // Status Filters
            let matchesStatus = true;
            if (statusFilters.pipeline) matchesStatus = matchesStatus && item.pipeline_status === 'Passed';
            if (statusFilters.merge) matchesStatus = matchesStatus && item.merge_status === 'Merged';
            if (statusFilters.prod_ready) matchesStatus = matchesStatus && item.production_ready === 'Yes';
            if (statusFilters.revert) matchesStatus = matchesStatus && item.is_reverted == 1;

            return matchesSearch && matchesDate && matchesEmployee && matchesProject && matchesBranch && matchesStatus;
        });
    }, [groupedActivity, searchTerm, dateRange, selectedEmployees, selectedProject, selectedBranch, statusFilters]);

    // Sort Logic
    const sortedActivity = useMemo(() => {
        let sortableItems = [...filteredActivity];
        if (sortConfig.key !== null) {
            sortableItems.sort((a, b) => {
                let aVal = a[sortConfig.key];
                let bVal = b[sortConfig.key];

                // Handle string comparisons case-insensitively
                if (typeof aVal === 'string') aVal = aVal.toLowerCase();
                if (typeof bVal === 'string') bVal = bVal.toLowerCase();

                if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
                if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }
        return sortableItems;
    }, [filteredActivity, sortConfig]);

    // Pagination Logic
    const totalPages = Math.ceil(sortedActivity.length / itemsPerPage);
    const paginatedActivity = sortedActivity.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const requestSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const handleExportExcel = async () => {
        try {
            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet('GitLab Activity');

            // 1. Title
            worksheet.mergeCells('A1:I1');
            const titleCell = worksheet.getCell('A1');
            titleCell.value = 'eNova Software and Hardware Solutions P Ltd';
            titleCell.alignment = { vertical: 'middle', horizontal: 'center' };
            titleCell.font = { name: 'Arial', size: 16, bold: true };

            // 2. SubTitle
            worksheet.mergeCells('A2:I2');
            const subTitleCell = worksheet.getCell('A2');
            subTitleCell.value = 'GitLab Activity Report';
            subTitleCell.alignment = { vertical: 'middle', horizontal: 'center' };
            subTitleCell.font = { name: 'Arial', size: 12, bold: true };

            // 3. Generation Date
            worksheet.mergeCells('A3:I3');
            const dateCell = worksheet.getCell('A3');
            dateCell.value = `Generated on: ${new Date().toLocaleString()}`;
            dateCell.alignment = { vertical: 'middle', horizontal: 'center' };
            dateCell.font = { name: 'Arial', size: 10, bold: false };

            // Spacer
            worksheet.addRow([]);

            // 4. Headers
            const headerTerms = ['Date', 'User', 'Project', 'Branch', 'Message', 'Pipeline', 'Merge Status', 'Prod Ready', 'Revert Status'];
            const headerRow = worksheet.addRow(headerTerms);

            headerRow.eachCell((cell) => {
                cell.font = { bold: true };
                cell.alignment = { horizontal: 'center' };
                cell.border = { bottom: { style: 'thin' } };
                cell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: 'FFEEEEEE' }
                };
            });

            // 5. Data
            sortedActivity.forEach(commit => {
                worksheet.addRow([
                    commit.commit_datetime,
                    commit.employee_email || '-',
                    commit.project_name,
                    commit.branch_name,
                    commit.commit_message,
                    commit.pipeline_status,
                    commit.merge_status,
                    commit.production_ready || 'No',
                    commit.is_reverted == 1 ? 'Reverted' : '-'
                ]);
            });

            // 6. Column Widths
            worksheet.columns = [
                { width: 22 }, // Date
                { width: 30 }, // User
                { width: 20 }, // Project
                { width: 20 }, // Branch
                { width: 50 }, // Message
                { width: 15 }, // Pipeline
                { width: 15 }, // Merge
                { width: 15 }, // Prod Ready
                { width: 15 }  // Revert
            ];

            // 7. Write and Save
            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            saveAs(blob, `GitLab_Activity_${new Date().toISOString().slice(0, 10)}.xlsx`);
        } catch (error) {
            console.error("Excel Export Error:", error);
            toast.error("Failed to export Excel");
        }
    };

    // Helper for Sort Icon
    const SortIcon = ({ column }) => {
        if (sortConfig.key !== column) return <span className="inline-block ml-1 w-3"></span>;
        return sortConfig.direction === 'asc'
            ? <FiChevronUp className="inline ml-1 text-blue-600" />
            : <FiChevronDown className="inline ml-1 text-blue-600" />;
    };

    // Helper for Pagination Buttons
    const getPageNumbers = () => {
        const pages = [];
        let start = Math.max(1, currentPage - 2);
        let end = Math.min(totalPages, start + 4);

        if (end - start < 4) {
            start = Math.max(1, end - 4);
        }

        for (let i = start; i <= end; i++) {
            pages.push(i);
        }
        return pages;
    };


    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-2 text-gray-500">Loading GitLab Activity...</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative" role="alert">
                <span className="block sm:inline">{error}</span>
            </div>
        );
    }

    if (!gitlabStats) return null;

    return (
        <div className="space-y-6">
            {/* GitLab User Profile */}
            {gitlabStats.gitlab_user && (
                <div className="bg-white rounded-xl shadow-md p-6 flex flex-col md:flex-row items-center md:items-start space-y-4 md:space-y-0 md:space-x-6 border-l-4 border-orange-500">
                    <img src={gitlabStats.gitlab_user.avatar_url} alt="GitLab Avatar" className="w-16 h-16 rounded-full border-2 border-gray-200 flex-shrink-0" />
                    <div className="text-center md:text-left overflow-hidden w-full">
                        <h3 className="text-xl font-bold text-gray-800 truncate">{gitlabStats.gitlab_user.name}</h3>
                        <div className="flex flex-col sm:flex-row items-center sm:space-x-4 text-sm text-gray-600 mt-1 space-y-1 sm:space-y-0 text-center sm:text-left justify-center md:justify-start">
                            <span className="flex items-center truncate max-w-full"><FiGitCommit className="mr-1 flex-shrink-0" /> @{gitlabStats.gitlab_user.username}</span>
                            <span className="flex items-center truncate max-w-full"><FiMail className="mr-1 flex-shrink-0" /> {gitlabStats.gitlab_user.email}</span>
                            <span className="bg-gray-100 px-2 py-0.5 rounded text-xs border border-gray-200 whitespace-nowrap">ID: {gitlabStats.gitlab_user.id}</span>
                        </div>
                        <a href={gitlabStats.gitlab_user.web_url} target="_blank" rel="noopener noreferrer" className="inline-block mt-2 text-blue-600 hover:text-blue-800 text-sm font-medium">View GitLab Profile &rarr;</a>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatCard icon={<FiGitCommit className="text-3xl" />} title="Total Commits" value={gitlabStats?.stats?.total_commits || 0} color="indigo" />
                <StatCard icon={<FiGitMerge className="text-3xl" />} title="Merged Commits" value={gitlabStats?.stats?.merged_commits || 0} color="violet" />
                <StatCard icon={<FiServer className="text-3xl" />} title="Production Ready" value={gitlabStats?.stats?.production_ready || 0} color="emerald" />

                <StatCard icon={<FiRotateCcw className="text-3xl" />} title="Reverted Commits" value={gitlabStats?.stats?.reverted_commits || 0} color="red" />
            </div>

            {/* Advanced Filters */}
            <div className="bg-white rounded-2xl shadow-sm p-4 md:p-5 border border-gray-200">
                <div className="flex flex-col space-y-4">
                    {/* Row 1: Date Range Filter (Centered) */}
                    <div className="flex justify-center w-full pb-2 border-b border-gray-100">
                        <div className="flex flex-col items-center">
                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Date Range</label>

                            <div className="flex flex-wrap items-center gap-2">
                                {/* Previous Day */}
                                <button
                                    onClick={goToPrevDay}
                                    className="p-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-500 transition-colors shadow-sm"
                                    title="Previous Day"
                                >
                                    <FiChevronLeft className="w-5 h-5" />
                                </button>

                                {/* From Date */}
                                <div
                                    className="relative flex items-center bg-gray-50 hover:bg-white border border-gray-200 rounded-lg px-3 py-2 cursor-pointer transition-all shadow-sm flex-1 min-w-[130px]"
                                    onClick={() => dateInputRef.current?.showPicker()}
                                >
                                    <FiCalendar className="w-4 h-4 text-gray-500 mr-2 flex-shrink-0" />
                                    <span className="text-sm font-medium text-gray-700">{formatDateDisplay(dateRange.from)}</span>
                                    <input
                                        type="date"
                                        ref={dateInputRef}
                                        value={dateRange.from}
                                        max={formatDate(new Date())}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            setDateRange(prev => {
                                                const newState = { ...prev, from: val };
                                                if (val > prev.to) newState.to = val;
                                                return newState;
                                            });
                                            // Trigger fetch after state update or manually here if needed
                                            // Ideally, useEffect monitors dateRange, but current logic calls fetchData manually in onChange
                                            // Let's stick to the previous pattern: call fetch immediately
                                            const newTo = (val > dateRange.to) ? val : dateRange.to;
                                            fetchData(val, newTo);
                                        }}
                                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                    />
                                </div>

                                <span className="text-sm font-medium text-gray-500">to</span>

                                {/* To Date */}
                                <div
                                    className="relative flex items-center bg-gray-50 hover:bg-white border border-gray-200 rounded-lg px-3 py-2 cursor-pointer transition-all shadow-sm flex-1 min-w-[130px]"
                                    onClick={() => toDateInputRef.current?.showPicker()}
                                >
                                    <FiCalendar className="w-4 h-4 text-gray-500 mr-2 flex-shrink-0" />
                                    <span className="text-sm font-medium text-gray-700">{formatDateDisplay(dateRange.to)}</span>
                                    <input
                                        type="date"
                                        ref={toDateInputRef}
                                        value={dateRange.to}
                                        min={dateRange.from}
                                        max={formatDate(new Date())}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            setDateRange(prev => ({ ...prev, to: val }));
                                            fetchData(dateRange.from, val);
                                        }}
                                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                    />
                                </div>

                                {/* Next Day */}
                                <button
                                    onClick={goToNextDay}
                                    disabled={dateRange.from >= formatDate(new Date())}
                                    className={`p-2 border border-gray-200 rounded-lg transition-colors shadow-sm ${dateRange.from >= formatDate(new Date())
                                        ? 'bg-gray-50 text-gray-300 cursor-not-allowed'
                                        : 'bg-white hover:bg-gray-50 text-gray-500'
                                        }`}
                                    title="Next Day"
                                >
                                    <FiChevronRight className="w-5 h-5" />
                                </button>

                                {/* Reset */}
                                <button
                                    onClick={goToToday}
                                    className="p-2 border border-gray-200 bg-white text-gray-600 rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
                                    title="Reset to Today"
                                >
                                    <FiRotateCcw className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Row 2: Secondary Filters (Employees, Project, Branch) */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

                        {/* Employees Multi-Select */}
                        <div className="flex flex-col">
                            <SearchableDropdown
                                label="Employees"
                                placeholder="All Employees"
                                options={uniqueEmployees.map(emp => ({ value: emp.email, label: emp.name }))}
                                value={selectedEmployees[0] || ''}
                                onChange={(val) => {
                                    const newSelected = val ? [val] : [];
                                    setSelectedEmployees(newSelected);
                                }}
                            />
                        </div>

                        {/* Project */}
                        <div className="flex flex-col">
                            <SearchableDropdown
                                label="Project"
                                placeholder="All Projects"
                                options={uniqueProjects.map(proj => ({ value: proj, label: proj }))}
                                value={selectedProject}
                                onChange={(val) => setSelectedProject(val)}
                            />
                        </div>

                        {/* Branch */}
                        <div className="flex flex-col">
                            <SearchableDropdown
                                label="Branch"
                                placeholder="All Branches"
                                options={uniqueBranches.map(branch => ({ value: branch, label: branch }))}
                                value={selectedBranch}
                                onChange={(val) => setSelectedBranch(val)}
                            />
                        </div>
                    </div>

                    {/* Row 2: Status Chips */}
                    <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-gray-100">
                        <span className="text-xs font-bold text-gray-400 uppercase tracking-wider mr-2">Status Filters:</span>

                        <button
                            onClick={() => setStatusFilters(prev => ({ ...prev, pipeline: !prev.pipeline }))}
                            className={`px-4 py-1.5 rounded-full text-xs font-bold border transition-colors flex items-center gap-2 ${statusFilters.pipeline ? 'bg-blue-100 border-blue-200 text-blue-700' : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'}`}
                        >
                            <span className={`w-2 h-2 rounded-full ${statusFilters.pipeline ? 'bg-blue-500' : 'bg-gray-400'}`}></span>
                            PIPELINE PASSED
                        </button>

                        <button
                            onClick={() => setStatusFilters(prev => ({ ...prev, merge: !prev.merge }))}
                            className={`px-4 py-1.5 rounded-full text-xs font-bold border transition-colors flex items-center gap-2 ${statusFilters.merge ? 'bg-purple-100 border-purple-200 text-purple-700' : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'}`}
                        >
                            <span className={`w-2 h-2 rounded-full ${statusFilters.merge ? 'bg-purple-500' : 'bg-gray-400'}`}></span>
                            MERGED
                        </button>

                        <button
                            onClick={() => setStatusFilters(prev => ({ ...prev, prod_ready: !prev.prod_ready }))}
                            className={`px-4 py-1.5 rounded-full text-xs font-bold border transition-colors flex items-center gap-2 ${statusFilters.prod_ready ? 'bg-emerald-100 border-emerald-200 text-emerald-700' : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'}`}
                        >
                            <span className={`w-2 h-2 rounded-full ${statusFilters.prod_ready ? 'bg-emerald-500' : 'bg-gray-400'}`}></span>
                            PROD READY
                        </button>

                        <button
                            onClick={() => setStatusFilters(prev => ({ ...prev, revert: !prev.revert }))}
                            className={`px-4 py-1.5 rounded-full text-xs font-bold border transition-colors flex items-center gap-2 ${statusFilters.revert ? 'bg-red-100 border-red-200 text-red-700' : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'}`}
                        >
                            <span className={`w-2 h-2 rounded-full ${statusFilters.revert ? 'bg-red-500' : 'bg-gray-400'}`}></span>
                            REVERTED
                        </button>
                    </div>
                </div>
            </div>

            {/* Recent Activity Table - Matching Employee Table UI */}
            <div className="bg-white rounded-2xl shadow-md overflow-hidden flex flex-col h-full w-full">
                {/* Toolbar */}
                <div className="p-6 border-b border-gray-200 flex flex-col md:flex-row justify-between items-center gap-4 bg-white">
                    <div className="flex items-center gap-2">
                        <h3 className="text-xl font-bold text-gray-800">GitLab Recent Activity</h3>
                        <span className="bg-blue-100 text-blue-800 text-xs font-semibold px-2.5 py-0.5 rounded-full">{filteredActivity.length}</span>
                        <button
                            onClick={() => fetchData(dateRange.from, dateRange.to)}
                            className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-colors ml-2"
                            title="Refresh Table"
                        >
                            <FiRotateCcw className={loading ? 'animate-spin' : ''} />
                        </button>
                    </div>

                    <div className="flex flex-col md:flex-row items-center gap-3 w-full md:w-auto">
                        {/* Page Size Selector */}
                        <div className="relative">
                            <select
                                value={itemsPerPage}
                                onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                                className="bg-gray-50 border border-gray-300 text-gray-600 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 py-2 pl-3 pr-8 outline-none cursor-pointer hover:bg-white transition-colors appearance-none"
                            >
                                <option value={5}>5</option>
                                <option value={10}>10</option>
                                <option value={20}>20</option>
                                <option value={50}>50</option>
                                <option value={9999}>All</option>
                            </select>
                        </div>

                        {/* Search */}
                        <div className="relative w-full md:w-64">
                            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search activity..."
                                value={searchTerm}
                                onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50 focus:bg-white transition-colors text-sm"
                            />
                        </div>

                        {/* Export */}
                        <div className="flex gap-2">
                            <button onClick={handleExportExcel} className="p-2 text-green-600 rounded hover:bg-green-50 transition-colors shadow-sm border border-gray-200" title="Export to Excel">
                                <RiFileExcelFill size={20} />
                            </button>
                        </div>
                    </div>
                </div>

                <div className="overflow-x-auto w-full flex-1">
                    <table className="w-full text-left border-collapse">
                        <thead className="bg-gray-50 sticky top-0 z-10">
                            <tr className="text-gray-600 text-xs font-bold uppercase tracking-wider border-b border-gray-200">
                                <th className="px-3 py-3 cursor-pointer hover:bg-gray-50 transition-colors" onClick={() => requestSort('commit_datetime')}>
                                    <div className="flex items-center">
                                        DATE <SortIcon column="commit_datetime" />
                                    </div>
                                </th>
                                <th className="px-10 py- cursor-pointer hover:bg-gray-50 transition-colors" onClick={() => requestSort('employee_email')}>
                                    <div className="flex items-center">
                                        USER <SortIcon column="employee_email" />
                                    </div>
                                </th>
                                <th className="px-3 py-3 cursor-pointer hover:bg-gray-50 transition-colors" onClick={() => requestSort('project_name')}>
                                    <div className="flex items-center">
                                        PROJECT <SortIcon column="project_name" />
                                    </div>
                                </th>
                                <th className="px-3 py-3 cursor-pointer hover:bg-gray-50 transition-colors" onClick={() => requestSort('branch_name')}>
                                    <div className="flex items-center">
                                        BRANCH <SortIcon column="branch_name" />
                                    </div>
                                </th>
                                <th className="px-3 py-3 cursor-pointer hover:bg-gray-50 transition-colors" onClick={() => requestSort('commit_message')}>
                                    <div className="flex items-center">
                                        MESSAGE <SortIcon column="commit_message" />
                                    </div>
                                </th>
                                <th className="px-3 py-3 cursor-pointer hover:bg-gray-50 transition-colors" onClick={() => requestSort('pipeline_status')}>
                                    <div className="flex items-center">
                                        PIPELINE <SortIcon column="pipeline_status" />
                                    </div>
                                </th>
                                <th className="px-3 py-3 cursor-pointer hover:bg-gray-50 transition-colors" onClick={() => requestSort('merge_status')}>
                                    <div className="flex items-center">
                                        MERGE <SortIcon column="merge_status" />
                                    </div>
                                </th>
                                <th className="px-3 py-3 cursor-pointer hover:bg-gray-50 transition-colors" onClick={() => requestSort('production_ready')}>
                                    <div className="flex items-center text-emerald-600">
                                        PROD READY <SortIcon column="production_ready" />
                                    </div>
                                </th>
                                <th className="px-3 py-3 cursor-pointer hover:bg-gray-50 transition-colors" onClick={() => requestSort('is_reverted')}>
                                    <div className="flex items-center">
                                        REVERT <SortIcon column="is_reverted" />
                                    </div>
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {paginatedActivity.map((group, idx) => {
                                const isReverted = group.is_reverted == 1;
                                return (
                                    <tr
                                        key={idx}
                                        className="hover:bg-blue-50 cursor-pointer transition-colors"
                                        onClick={() => setSelectedCommit(group)}
                                    >
                                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-600">
                                            {new Date(group.latest_commit_datetime).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                                        </td>
                                        <td className="px-3 py-2">
                                            <div className="flex items-center space-x-2">
                                                <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-xs font-bold">
                                                    {group.first_name ? group.first_name[0] : (group.employee_email ? group.employee_email[0].toUpperCase() : 'U')}
                                                </div>
                                                <div className="flex flex-col overflow-hidden max-w-[150px]">
                                                    {group.first_name ? (
                                                        <>
                                                            <span className="text-xs text-gray-900 font-bold truncate">{group.first_name} {group.last_name}</span>
                                                            <span className="text-[10px] text-gray-500 truncate">{group.employee_email}</span>
                                                        </>
                                                    ) : (
                                                        <span className="text-xs text-gray-900 font-medium truncate">{group.employee_email || 'Unknown User'}</span>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-3 py-2 font-medium text-sm text-gray-800">{group.project_name}</td>
                                        <td className="px-3 py-2">
                                            <div className="flex flex-wrap gap-1 max-w-[150px]">
                                                {(() => {
                                                    let visibleBranches = [...group.display_branches];
                                                    // If Prod Ready but master/main missing, add master (visual indicator)
                                                    if (group.production_ready === 'Yes' &&
                                                        !visibleBranches.some(b => ['master', 'main'].includes(b))) {
                                                        visibleBranches.push('master');
                                                    }
                                                    return visibleBranches.map((b, i) => (
                                                        <span key={i} className={`px-2 py-0.5 rounded text-[10px] border font-mono truncate max-w-full ${['master', 'main'].includes(b) ? 'bg-purple-50 text-purple-700 border-purple-100' : 'bg-blue-50 text-blue-700 border-blue-100'
                                                            }`}>{b}</span>
                                                    ));
                                                })()}
                                            </div>
                                        </td>
                                        <td className="px-3 py-2 max-w-xs truncate text-sm text-gray-600" title={group.commit_message}>{group.commit_message}</td>
                                        <td className="px-3 py-2">
                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${group.pipeline_status === 'Passed' ? 'bg-green-100 text-green-700' :
                                                group.pipeline_status === 'Failed' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'
                                                }`}>
                                                {group.pipeline_status}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2">
                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${group.merge_status === 'Merged' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'
                                                }`}>
                                                {group.merge_status}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2">
                                            <div className="flex flex-col">
                                                <span className={`px-2 py-1 rounded-full text-xs font-medium w-fit ${group.production_ready === 'Yes' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'
                                                    }`}>
                                                    {group.production_ready || 'No'}
                                                </span>
                                                {group.deployed_by_user && <span className="text-[9px] text-gray-400 mt-0.5 ml-1">by {group.deployed_by_user}</span>}
                                            </div>
                                        </td>
                                        <td className="px-3 py-2">
                                            {isReverted ? (
                                                <span className="flex items-center text-red-600 font-medium text-xs bg-red-50 px-2 py-1 rounded border border-red-100 w-fit">
                                                    <FiRotateCcw className="mr-1" /> Reverted
                                                </span>
                                            ) : (
                                                <span className="text-gray-400">-</span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                            {paginatedActivity.length === 0 && (
                                <tr>
                                    <td colSpan="8" className="px-6 py-20 text-center text-gray-500">
                                        <div className="flex flex-col items-center justify-center">
                                            <div className="bg-gray-100 p-4 rounded-full mb-3"><FiSearch className="text-3xl text-gray-400" /></div>
                                            <p className="text-lg font-medium text-gray-900">No activity found</p>
                                            <p className="text-sm text-gray-500 mt-1">Try adjusting your search or filters</p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination Controls */}
                <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 flex flex-col md:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-3 text-sm text-gray-600">
                        <span className="hidden md:inline">
                            Showing <span className="font-semibold text-gray-900">{filteredActivity.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0}</span> to <span className="font-semibold text-gray-900">{Math.min(currentPage * itemsPerPage, filteredActivity.length)}</span> of <span className="font-semibold text-gray-900">{filteredActivity.length}</span> results
                        </span>
                    </div>

                    <div className="flex items-center gap-1">
                        <button
                            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                            disabled={currentPage === 1}
                            className="p-2 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed text-gray-600 transition"
                        >
                            <FiChevronLeft />
                        </button>

                        <div className="flex items-center gap-1">
                            {getPageNumbers().map(pageNum => (
                                <button
                                    key={pageNum}
                                    onClick={() => setCurrentPage(pageNum)}
                                    className={`w-8 h-8 rounded text-sm font-medium transition-colors ${currentPage === pageNum
                                        ? 'bg-[#C30010] text-white shadow-sm'
                                        : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                                        }`}
                                >
                                    {pageNum}
                                </button>
                            ))}
                        </div>

                        <button
                            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                            disabled={currentPage === totalPages || totalPages === 0}
                            className="p-2 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed text-gray-600 transition"
                        >
                            <FiChevronRight />
                        </button>
                    </div>
                </div>
            </div>
            {/* Commit Details Modal */}
            {selectedCommit && (
                <CommitDetailsModal
                    commit={selectedCommit}
                    onClose={() => setSelectedCommit(null)}
                    allActivities={gitlabStats?.recent_activity || []}
                />
            )}
        </div>
    );
};

// Internal Modal Component for Commit Details
const CommitDetailsModal = ({ commit, onClose }) => {
    // commit is grouped object. Use lineage_events or fallback to [commit]
    const events = commit.lineage_events || [commit];

    // Logic to find contributors if it's a merge commit
    const contributors = useMemo(() => {
        const uniqueContributors = new Map();
        events.forEach(evt => {
            // Normalize email to avoid duplicates like 'Name <Email>' vs 'email' or case
            const email = evt.employee_email ? evt.employee_email.toLowerCase().trim() : '';
            if (email && !uniqueContributors.has(email)) {
                uniqueContributors.set(email, evt);
            }
        });
        return Array.from(uniqueContributors.values());
    }, [events]);

    // Optimize Timeline Events - Deduplicate excess "Production Ready" entries
    const timelineEvents = useMemo(() => {
        let finalEvents = [...events];

        // CHECK: Do we have a definitive Master/Main Production event?
        const hasMasterProd = finalEvents.some(e =>
            e.production_ready === 'Yes' && ['master', 'main'].includes(e.branch_name)
        );

        // CHECK: Is the group considered Production Ready? (At least one commit is marked Yes)
        // We check if ANY record in this lineage is Prod Ready.
        const isProductionReady = finalEvents.some(e => e.production_ready === 'Yes');

        // INJECT: If Prod Ready but missing the specific Master event (e.g. for contributors branched off earlier),
        // we synthesize a "Production Ready" event to ensure they see the deployment milestone.
        if (isProductionReady && !hasMasterProd) {
            // Find a reference event to copy metadata fro (e.g. the one that has deployed_by)
            const refEvent = finalEvents.find(e => e.production_ready === 'Yes') || finalEvents[0];

            // Calculate a timestamp slightly ahead of the latest existing event to ensure it sits on top (plus 1 second)
            const latestTime = Math.max(...finalEvents.map(e => new Date(e.commit_datetime).getTime()));
            const nextReviewTime = new Date(latestTime + 1000).toISOString();

            finalEvents.push({
                ...refEvent,
                branch_name: 'master', // Force it to show as Master
                production_ready: 'Yes',
                commit_datetime: nextReviewTime,
                is_synthetic: true // flag for debugging if needed
            });
        }

        // We want to show ALL events to preserve history (Pushed -> Merged -> Prod).
        // The display logic (JSX) will handle showing "Production Ready" ONLY for the master event.
        // Sort by date (descending), then by Importance (Prod Ready > Merged > Pushed) to handle same-second ties
        return finalEvents.sort((a, b) => {
            const dateA = new Date(a.commit_datetime).getTime();
            const dateB = new Date(b.commit_datetime).getTime();
            if (dateB !== dateA) return dateB - dateA;

            // Tie-breaker: Prod Ready First
            if (b.production_ready === 'Yes' && ['master', 'main'].includes(b.branch_name)) return 1;
            if (a.production_ready === 'Yes' && ['master', 'main'].includes(a.branch_name)) return -1;
            return 0;
        });
    }, [events, commit]);

    const files = JSON.parse(commit.files_changed || '[]');

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden animate-in zoom-in-95 duration-200">
                {/* Header */}
                <div className="p-6 border-b border-gray-100 flex justify-between items-start bg-gradient-to-r from-gray-50 to-white">
                    <div>
                        <div className="flex items-center space-x-2 text-blue-600 mb-1">
                            <FiGitCommit />
                            <span className="text-xs font-mono font-bold uppercase tracking-wider">Commit Detail</span>
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 leading-tight">
                            {commit.commit_message || 'No message'}
                        </h3>
                        <p className="text-sm text-gray-500 mt-1 font-mono">{commit.commit_id}</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-400 hover:text-gray-600"
                    >
                        <FiX size={24} />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-6 space-y-8">

                    {/* Contributors Section (New) */}
                    {contributors.length > 0 && (
                        <div>
                            <h4 className="flex items-center text-sm font-bold text-gray-700 uppercase tracking-wider mb-4">
                                <FiUser className="mr-2" /> Contributors
                            </h4>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {contributors.map((contrib, idx) => (
                                    <div key={idx} className="flex items-center gap-3 p-3 bg-indigo-50 rounded-xl border border-indigo-100">
                                        <div className="w-8 h-8 rounded-full bg-indigo-200 flex items-center justify-center text-indigo-700 font-bold text-xs">
                                            {contrib.first_name ? contrib.first_name[0] : (contrib.employee_email ? contrib.employee_email[0] : '?')}
                                        </div>
                                        <div className="overflow-hidden">
                                            <p className="text-sm font-bold text-gray-900 truncate">
                                                {contrib.first_name ? `${contrib.first_name} ${contrib.last_name || ''}` : contrib.employee_email}
                                            </p>
                                            <p className="text-xs text-indigo-600 truncate">{contrib.employee_email}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Stats & Info Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="bg-green-50 rounded-xl p-4 border border-green-100">
                            <span className="text-green-600 text-xs font-bold uppercase block mb-1">Additions</span>
                            <span className="text-2xl font-bold text-green-700">+{commit.lines_added || 0}</span>
                        </div>
                        <div className="bg-red-50 rounded-xl p-4 border border-red-100">
                            <span className="text-red-600 text-xs font-bold uppercase block mb-1">Deletions</span>
                            <span className="text-2xl font-bold text-red-700">-{commit.lines_deleted || 0}</span>
                        </div>
                        <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                            <span className="text-blue-600 text-xs font-bold uppercase block mb-1">Files</span>
                            <span className="text-2xl font-bold text-blue-700">{files.length}</span>
                        </div>
                        <div className="bg-purple-50 rounded-xl p-4 border border-purple-100">
                            <span className="text-purple-600 text-xs font-bold uppercase block mb-1">Status</span>
                            <span className="text-sm font-bold text-purple-700 block mt-1">{commit.merge_status}</span>
                        </div>
                    </div>

                    {/* Timeline */}
                    <div>
                        <h4 className="flex items-center text-sm font-bold text-gray-700 uppercase tracking-wider mb-4">
                            <FiClock className="mr-2" /> Activity Timeline
                        </h4>
                        <div className="space-y-4 relative before:absolute before:inset-y-0 before:left-3 before:w-0.5 before:bg-gray-100">
                            {timelineEvents.map((evt, idx) => (
                                <TimelineItem
                                    key={idx}
                                    icon={
                                        evt.is_reverted == 1 ? <FiRotateCcw className="text-red-500" /> :
                                            (evt.production_ready === 'Yes' && ['master', 'main'].includes(evt.branch_name)) ? <FiServer className="text-emerald-500" /> :
                                                evt.merge_status === 'Merged' ? <FiGitMerge className="text-purple-500" /> :
                                                    <FiGitCommit className="text-blue-500" />
                                    }
                                    title={
                                        evt.is_reverted == 1 ? 'Reverted' :
                                            (evt.production_ready === 'Yes' && ['master', 'main'].includes(evt.branch_name)) ? 'Production Ready' :
                                                evt.merge_status === 'Merged' ? 'Request Merged' :
                                                    'Code Pushed'
                                    }
                                    subtitle={
                                        <div>
                                            <span>to <b>{evt.branch_name}</b> {
                                                (evt.production_ready === 'Yes' && ['master', 'main'].includes(evt.branch_name) && evt.deployed_by) ? <span>by {evt.deployed_by}</span> : ''
                                            }</span>
                                        </div>
                                    }
                                    time={new Date(evt.commit_datetime).toLocaleString()}
                                    active={true}
                                    color={evt.is_reverted == 1 ? "red" : (evt.production_ready === 'Yes' && ['master', 'main'].includes(evt.branch_name)) ? "emerald" : evt.merge_status === 'Merged' ? "purple" : "gray"}
                                    user={(evt.production_ready === 'Yes' && ['master', 'main'].includes(evt.branch_name)) ? null : {
                                        first_name: evt.first_name,
                                        last_name: evt.last_name,
                                        email: evt.employee_email
                                    }}
                                />
                            ))}
                        </div>
                    </div>

                    {/* Files & Code Changes */}
                    <div>
                        <h4 className="flex items-center text-sm font-bold text-gray-700 uppercase tracking-wider mb-4">
                            <FiFileText className="mr-2" /> Code Changes
                        </h4>
                        <div className="space-y-4">
                            {files.length > 0 ? files.map((file, i) => {
                                const filePath = typeof file === 'string' ? file : file.path;
                                const diff = typeof file === 'string' ? null : file.diff;

                                return (
                                    <div key={i} className="border border-gray-100 rounded-xl overflow-hidden shadow-sm">
                                        <div className="bg-gray-50 px-4 py-3 flex items-center justify-between border-b border-gray-100">
                                            <div className="flex items-center">
                                                <span className="w-2 h-2 rounded-full bg-blue-400 mr-3"></span>
                                                <span className="text-sm font-mono text-gray-700 font-bold">{filePath}</span>
                                            </div>
                                            {diff && (
                                                <span className="text-[10px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded uppercase font-bold tracking-tighter">Live Diff</span>
                                            )}
                                        </div>
                                        {diff ? (
                                            <div className="bg-white p-4 overflow-x-auto">
                                                <pre className="text-xs font-mono space-y-0.5">
                                                    {diff.split('\n').map((line, lineIdx) => {
                                                        const isAddition = line.startsWith('+');
                                                        const isDeletion = line.startsWith('-');
                                                        const isHeader = line.startsWith('@@');

                                                        let bgColor = 'text-gray-500';
                                                        if (isAddition) bgColor = 'bg-green-50 text-green-700';
                                                        if (isDeletion) bgColor = 'bg-red-50 text-red-700';
                                                        if (isHeader) bgColor = 'text-blue-400 bg-blue-50/50';

                                                        return (
                                                            <div key={lineIdx} className={`${bgColor} px-1 rounded-sm`}>
                                                                {line}
                                                            </div>
                                                        );
                                                    })}
                                                </pre>
                                            </div>
                                        ) : (
                                            <div className="bg-white px-4 py-3 text-xs text-gray-400 italic">
                                                Full diff not available for this legacy record.
                                            </div>
                                        )}
                                    </div>
                                );
                            }) : (
                                <div className="px-4 py-12 text-center text-gray-400 border border-dashed border-gray-200 rounded-2xl">
                                    <FiFileText className="mx-auto mb-2 opacity-20" size={32} />
                                    No file changes recorded for this commit.
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="p-6 border-t border-gray-100 bg-gray-50">
                    <button
                        onClick={onClose}
                        className="w-full py-3 bg-white border border-gray-200 text-gray-700 font-bold rounded-xl hover:bg-gray-100 transition-colors shadow-sm"
                    >
                        Close Details
                    </button>
                </div>
            </div>
        </div>
    );
};

// Helper Item for Timeline
const TimelineItem = ({ icon, title, subtitle, time, color = 'blue', active = false, user }) => {
    const colors = {
        blue: 'bg-blue-100 text-blue-600',
        red: 'bg-red-100 text-red-600',
        purple: 'bg-purple-100 text-purple-600',
        emerald: 'bg-emerald-100 text-emerald-600'
    };

    return (
        <div className="relative pl-10">
            <div className={`absolute left-0 top-0 w-6 h-6 rounded-full flex items-center justify-center z-10 ${active ? colors[color] : 'bg-gray-100 text-gray-400'}`}>
                {React.cloneElement(icon, { size: 12 })}
            </div>
            <div>
                <div className="flex justify-between items-center">
                    <h5 className="text-sm font-bold text-gray-800">{title}</h5>
                    <span className="text-[10px] font-bold text-gray-400 uppercase">{time}</span>
                </div>
                <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>
                {user && (
                    <div className="flex items-center mt-1.5">
                        <div className="w-4 h-4 rounded-full bg-gray-200 flex items-center justify-center text-[8px] font-bold text-gray-600 mr-1.5">
                            {user.first_name ? user.first_name[0] : (user.email ? user.email[0].toUpperCase() : 'U')}
                        </div>
                        <span className="text-xs text-gray-600 font-medium">{user.first_name ? `${user.first_name} ${user.last_name || ''}` : user.email}</span>
                    </div>
                )}
            </div>
        </div>
    );
};

// Simple StatCard component
function StatCard({ icon, title, value, color }) {
    const colorClasses = {
        indigo: 'bg-indigo-100 text-indigo-600',
        violet: 'bg-violet-100 text-violet-600',
        emerald: 'bg-emerald-100 text-emerald-600',
        blue: 'bg-blue-100 text-blue-600',
        green: 'bg-green-100 text-green-600',
        purple: 'bg-purple-100 text-purple-600',
        amber: 'bg-amber-100 text-amber-600',
        red: 'bg-red-100 text-red-600'
    };

    return (
        <div className="bg-white rounded-xl shadow-md hover:shadow-lg transition p-6">
            <div className="flex items-center justify-between">
                <div className="flex-1">
                    <p className="text-gray-500 text-sm font-medium mb-2">{title}</p>
                    <h3 className="text-3xl font-bold text-gray-800">{value}</h3>
                </div>
                <div className={`w-14 h-14 rounded-lg flex items-center justify-center ${colorClasses[color] || 'bg-gray-100'}`}>{icon}</div>
            </div>
        </div>
    );
}

// Custom Searchable Dropdown Component
const SearchableDropdown = ({ options, value, onChange, placeholder, label }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const containerRef = React.useRef(null);

    // Filter options based on search
    const filteredOptions = useMemo(() => {
        return options.filter(opt =>
            opt.label.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [options, searchTerm]);

    // Find current label
    const currentLabel = options.find(o => o.value === value)?.label || placeholder;

    // Handle click outside to close
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div className="flex flex-col relative" ref={containerRef}>
            {label && <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">{label}</label>}

            {/* Trigger Button */}
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className="w-full bg-gray-50 border border-gray-300 text-gray-700 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 p-2.5 outline-none flex justify-between items-center transition-colors hover:bg-white"
            >
                <span className="truncate mr-2">{value ? currentLabel : placeholder}</span>
                <FiChevronDown className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''} text-gray-400`} />
            </button>

            {/* Dropdown Menu */}
            {isOpen && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-50 overflow-hidden animate-in fade-in zoom-in-95 duration-100">
                    {/* Search Input */}
                    <div className="p-2 border-b border-gray-100 bg-gray-50">
                        <div className="relative">
                            <FiSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
                            <input
                                type="text"
                                className="w-full pl-8 pr-3 py-1.5 text-sm bg-white border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-gray-400"
                                placeholder="Search..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                autoFocus
                                onClick={(e) => e.stopPropagation()}
                            />
                        </div>
                    </div>

                    {/* Options List */}
                    <div className="max-h-60 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-200 scrollbar-track-transparent">
                        <div
                            className={`px-4 py-2 cursor-pointer hover:bg-blue-50 text-sm transition-colors ${!value ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'}`}
                            onClick={() => {
                                onChange('');
                                setIsOpen(false);
                                setSearchTerm('');
                            }}
                        >
                            {placeholder}
                        </div>
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map((opt, idx) => (
                                <div
                                    key={idx}
                                    className={`px-4 py-2 cursor-pointer hover:bg-blue-50 text-sm transition-colors flex justify-between items-center ${value === opt.value ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'}`}
                                    onClick={() => {
                                        onChange(opt.value);
                                        setIsOpen(false);
                                        setSearchTerm('');
                                    }}
                                >
                                    <span className="truncate">{opt.label}</span>
                                    {value === opt.value && <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>}
                                </div>
                            ))
                        ) : (
                            <div className="px-4 py-3 text-xs text-gray-400 text-center italic">
                                No results found
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};


export default GitActivity;
